import { useAuth } from '@teko/auth-react';
import { Button, Flex, Quantity, Tooltip } from '@teko/ds-desktop';
import { ProductCard } from '@teko/teko-ecommerce';
import { ProductCardProps } from '@teko/types/ecommerce';
import { useHamper, useTenantConfig } from 'contexts';
import { requiredConfirm18YearsOld } from 'helpers';
import { t } from 'i18n';
import { IHamperItemData } from 'types';

export enum EHamperCardType {
  HAMPER = 'hamper',
  HAMPER_ITEM = 'hamperItem',
}

type HamperCardProps = ProductCardProps & {
  hamperItemKey?: string;
  hamperCardType?: EHamperCardType;
  setShowPopupCheckAge?: (value: boolean) => void;
};

export const HamperCard = (props: HamperCardProps) => {
  const {
    hamperItemKey = '',
    hamperCardType = EHamperCardType.HAMPER,
    setShowPopupCheckAge,
    ...rest
  } = props;

  const {
    hamperData,
    addHamper,
    minusHamper,
    removeHamper,
    onUpdateConfig,
    removeHamperItem,
    setHamperItemQuantity,
    onChangeQuantityHamper,
    increaseHamperItemQuantity,
    decreaseHamperItemQuantity,
  } = useHamper();
  const { isMobile } = useTenantConfig();
  const { user, loading: authLoading } = useAuth();

  const hamperQuantity =
    hamperData?.hampers?.find((h) => h.sku === props.sku)?.quantity || 0;

  const hamperItemQty =
    (
      hamperData.configs[hamperData.activeIndex][
        hamperItemKey
      ] as IHamperItemData[]
    )?.find((h) => h.sku === props.sku)?.quantity || 0;

  const quantity =
    hamperCardType === EHamperCardType.HAMPER ? hamperQuantity : hamperItemQty;

  const handleAdd = (sku: string) => {
    if (authLoading) return;
    const isUnder18 =
      requiredConfirm18YearsOld(user) && !!rest?.isAlcoholicCategory;
    const onAddHamper = () => {
      if (hamperCardType === EHamperCardType.HAMPER) {
        addHamper(sku);
      } else {
        increaseHamperItemQuantity(hamperItemKey, sku);
      }
    };

    if (isUnder18 && setShowPopupCheckAge) {
      setShowPopupCheckAge(true);
    } else onAddHamper();
  };

  const handleMinus = (sku: string) => {
    if (hamperCardType === EHamperCardType.HAMPER) {
      minusHamper(sku);
    } else {
      decreaseHamperItemQuantity(hamperItemKey, sku);
    }
  };

  const handleRemove = (sku: string) => {
    if (hamperCardType === EHamperCardType.HAMPER) {
      removeHamper(sku);
    } else {
      removeHamperItem(hamperItemKey, sku);
    }
  };

  const handleChangeQuantity = (sku: string, quantity: number) => {
    if (hamperCardType === EHamperCardType.HAMPER) {
      onChangeQuantityHamper(sku, quantity);
    } else {
      setHamperItemQuantity(hamperItemKey, quantity, sku);
    }
  };

  return (
    <ProductCard
      {...rest}
      customButtons={
        <Flex
          direction={isMobile ? 'column' : 'row'}
          width="100%"
          align="center"
        >
          {hamperCardType === EHamperCardType.HAMPER && (
            <Flex width={isMobile ? '100%' : '50%'}>
              {isMobile ? (
                <Button
                  variant="secondary"
                  size="s"
                  block
                  onClick={() => onUpdateConfig?.(props)}
                  marginBottom="s12"
                >
                  {t('Tuỳ chỉnh')}
                </Button>
              ) : (
                <Tooltip
                  title={t('Thay đổi thành phần để tạo giỏ quà tự chọn')}
                  placement="top"
                >
                  <Button
                    variant="secondary"
                    size="s"
                    block
                    paddingLeft="s16"
                    paddingRight="s16"
                    onClick={() => onUpdateConfig?.(props)}
                  >
                    {t('Tuỳ chỉnh')}
                  </Button>
                </Tooltip>
              )}
            </Flex>
          )}
          <Flex
            width={
              isMobile || hamperCardType !== EHamperCardType.HAMPER
                ? '100%'
                : '50%'
            }
          >
            {quantity > 0 ? (
              <Quantity
                block
                quantity={quantity}
                setQuantity={(newQuantity) =>
                  handleChangeQuantity(props.sku, newQuantity)
                }
                onMinus={
                  quantity === 1
                    ? () => handleRemove(props.sku)
                    : () => handleMinus(props.sku)
                }
                onPlus={() => handleAdd(props.sku)}
                min={0}
                max={999}
                color="primary500"
              />
            ) : (
              <Button
                variant="primary"
                size="s"
                block
                padding="s0"
                onClick={(
                  e: React.MouseEvent<HTMLButtonElement> &
                    React.MouseEvent<HTMLAnchorElement>,
                ) => {
                  e.preventDefault();
                  handleAdd(props.sku);
                }}
              >
                {t('Chọn')}
              </Button>
            )}
          </Flex>
        </Flex>
      }
    />
  );
};
